// src/services/spatialService.js
//
// FlowSense MOW+ — 4D Subsurface Clearance API client
// Client counterpart to backend/services/spatialCoordinationService.js
//
// =============================================================================
// SAFETY CONTRACT — READ BEFORE MODIFYING
// =============================================================================
// The backend was hardened to fail CLOSED. That guarantee is worthless if the
// client re-opens it. The two ways a frontend silently re-opens a fail-closed
// backend are:
//
//   1. Throwing on transport failure, so a `catch {}` upstream leaves the UI
//      showing its default (usually "no conflicts") state.
//   2. Rendering a stale response — the user moves the map pin, a late reply
//      for the OLD coordinate lands, and the panel shows CLEAR for a site that
//      was never scanned.
//
// This module therefore NEVER throws for transport failures. It always resolves
// to a normalised ClearanceResult, and any outcome that is not a verified CLEAR
// resolves as INDETERMINATE. Superseded requests resolve as SUPERSEDED with
// `ignorable: true` so callers can drop them instead of flashing a false alarm.
//
// =============================================================================
// DEPLOYMENT ORDERING
// =============================================================================
// Deploy the backend BEFORE this client. Against a pre-hardening backend the
// legacy `{ hasConflict: false }` payload is ambiguous — it means either "no
// conflicts" or "the scan crashed". This client marks such responses
// `degraded: true` and surfaces a warning rather than trusting them.
// =============================================================================

import api from './api';

// --- UPDATE: shared contract constants (mirror the backend service) ---------
export const CLEARANCE_STATUS = Object.freeze({
    CLEAR: 'CLEAR',                 // Verified: scan completed, nothing found.
    CONFLICT: 'CONFLICT',           // Verified: hits found. Requires clearance.
    INDETERMINATE: 'INDETERMINATE', // Scan did not complete. Treat as blocking.
    SUPERSEDED: 'SUPERSEDED',       // Request cancelled by a newer one. Ignore.
});

export const CLEARANCE_SEVERITY = Object.freeze({
    NONE: 'NONE', LOW: 'LOW', MEDIUM: 'MEDIUM', HIGH: 'HIGH', CRITICAL: 'CRITICAL',
});

const SEVERITY_RANK = Object.freeze({ NONE: 0, LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 });

const CONFIG = Object.freeze({
    ENDPOINT: '/spatial/evaluate-clearance',
    REQUEST_TIMEOUT_MS: 12_000,   // Island mobile links stall; never hang forever.
    TOTAL_DEADLINE_MS: 30_000,    // Budget shared across all retry attempts.
    MAX_RETRIES: 2,
    RETRY_BASE_DELAY_MS: 400,
    RETRYABLE_HTTP: new Set([408, 425, 429, 500, 502, 503, 504]),
});

const GENERIC_BLOCK_MESSAGE =
    'Subsurface clearance could not be verified. Manual utility locate required before excavation.';
// --- END UPDATE

// --- UPDATE: helpers --------------------------------------------------------

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function newRequestId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Formats a date as YYYY-MM-DD in LOCAL time.
 *
 * A raw `Date` handed to axios is serialised by JSON.stringify via
 * toISOString() — i.e. converted to UTC. At UTC-4 any evening timestamp arrives
 * at the backend as the PREVIOUS calendar day, shifting the entire moratorium
 * window. This reproduces, on the client, the exact bug that was just fixed in
 * the backend, so the date is normalised before it ever reaches the wire.
 */
export function toLocalDateString(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Client-side pre-flight. Mirrors the backend gate so an unmappable work order
 * blocks instantly instead of burning a round trip — and, critically, uses
 * Number.isFinite rather than a truthiness check, which is false at 0.
 */
function validateParams({ latitude, longitude, plannedDate, excavationDepthM }) {
    const errors = [];

    const lat = typeof latitude === 'string' ? Number(latitude) : latitude;
    const lng = typeof longitude === 'string' ? Number(longitude) : longitude;

    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
        errors.push('Work order has no valid latitude');
    }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
        errors.push('Work order has no valid longitude');
    }

    const date = toLocalDateString(plannedDate);
    if (!date) errors.push('Planned date is missing or unreadable');

    let depth = typeof excavationDepthM === 'string' ? Number(excavationDepthM) : excavationDepthM;
    if (depth === undefined || depth === null || depth === '') depth = 1.5;
    if (!Number.isFinite(depth) || depth <= 0 || depth > 30) {
        errors.push('Excavation depth must be between 0 and 30 metres');
    }

    return { errors, lat, lng, date, depth };
}

function isAbortError(error) {
    return (
        error?.name === 'AbortError' ||
        error?.name === 'CanceledError' ||
        error?.code === 'ERR_CANCELED' ||
        error?.message === 'canceled'
    );
}

function isTimeoutError(error) {
    return error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT';
}

/**
 * Distinguishes "the server answered" from "we never reached the server".
 * A missing `error.response` on a POST means the request may or may not have
 * been executed server-side — which is exactly why the audit trail is written
 * by the backend and not inferred here.
 */
function classifyTransportError(error) {
    if (isAbortError(error)) return 'ABORTED';
    if (isTimeoutError(error)) return 'TIMEOUT';
    if (!error?.response) return 'NETWORK';

    const s = error.response.status;
    if (s === 401) return 'UNAUTHENTICATED';
    if (s === 403) return 'FORBIDDEN';
    if (s === 409) return 'CONFLICT_RESPONSE';   // assertClearance() rejection — has a body
    if (s === 422 || s === 400) return 'VALIDATION';
    if (s >= 500) return 'SERVER';
    return 'CLIENT';
}
// --- END UPDATE

// --- UPDATE: result normalisation ------------------------------------------

function baseResult(overrides = {}) {
    return {
        status: CLEARANCE_STATUS.INDETERMINATE,
        hasConflict: true,
        requiresManualClearance: true,
        degraded: true,
        ignorable: false,
        severity: CLEARANCE_SEVERITY.CRITICAL,
        utilityCollisions: [],
        temporalConflicts: [],
        scanRadiusM: null,
        evaluationId: null,
        evaluatedAt: new Date().toISOString(),
        durationMs: null,
        warnings: [],
        error: GENERIC_BLOCK_MESSAGE,
        errorKind: null,
        ...overrides,
    };
}

/**
 * Converts a server payload into the canonical shape.
 *
 * Any payload this function does not positively recognise as a completed scan
 * becomes INDETERMINATE. A 200 response carrying a proxy interstitial, an HTML
 * login page, or `undefined` must never be read as "clear".
 */
function normalizeClearance(data, meta = {}) {
    const warnings = Array.isArray(data?.warnings) ? [...data.warnings] : [];

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return baseResult({
            errorKind: 'MALFORMED_RESPONSE',
            error: 'The clearance service returned an unreadable response. ' + GENERIC_BLOCK_MESSAGE,
            ...meta,
        });
    }

    const utilityCollisions = Array.isArray(data.utilityCollisions) ? data.utilityCollisions : [];
    const temporalConflicts = Array.isArray(data.temporalConflicts) ? data.temporalConflicts : [];

    // Hardened backend: an explicit status field.
    if (typeof data.status === 'string' && CLEARANCE_STATUS[data.status]) {
        const status = data.status;
        return {
            ...baseResult(),
            status,
            hasConflict: status !== CLEARANCE_STATUS.CLEAR,
            requiresManualClearance: data.requiresManualClearance ?? status !== CLEARANCE_STATUS.CLEAR,
            degraded: data.degraded === true,
            severity: data.severity ?? (status === CLEARANCE_STATUS.CLEAR ? CLEARANCE_SEVERITY.NONE : CLEARANCE_SEVERITY.CRITICAL),
            utilityCollisions,
            temporalConflicts,
            scanRadiusM: data.scanRadiusM ?? null,
            evaluationId: data.evaluationId ?? null,
            evaluatedAt: data.evaluatedAt ?? new Date().toISOString(),
            durationMs: data.durationMs ?? null,
            warnings,
            error: status === CLEARANCE_STATUS.CLEAR ? null : (data.error ?? null),
            errorKind: null,
            ...meta,
        };
    }

    // Legacy backend: `hasConflict` only. A false value here is ambiguous —
    // the pre-hardening service returned it on internal errors too — so a
    // "clear" from this shape is reported as degraded and unverified.
    if (typeof data.hasConflict === 'boolean') {
        const conflicted = data.hasConflict || utilityCollisions.length > 0 || temporalConflicts.length > 0;
        warnings.push(
            'Clearance service is running a pre-hardening contract; a clear result from this version is unverified.'
        );
        return {
            ...baseResult(),
            status: conflicted ? CLEARANCE_STATUS.CONFLICT : CLEARANCE_STATUS.CLEAR,
            hasConflict: conflicted,
            requiresManualClearance: true,
            degraded: true,
            severity: conflicted ? CLEARANCE_SEVERITY.HIGH : CLEARANCE_SEVERITY.LOW,
            utilityCollisions,
            temporalConflicts,
            warnings,
            error: null,
            errorKind: 'LEGACY_CONTRACT',
            ...meta,
        };
    }

    return baseResult({
        errorKind: 'UNKNOWN_CONTRACT',
        error: 'The clearance service returned an unrecognised payload. ' + GENERIC_BLOCK_MESSAGE,
        warnings,
        ...meta,
    });
}

/**
 * A 409 from the backend's assertClearance() guard carries the full conflict
 * detail in its body. The original client threw it away and surfaced a bare
 * "Request failed with status code 409" — losing exactly the information the
 * crew needs.
 */
function normalizeConflictResponse(error, meta) {
    const body = error?.response?.data ?? {};
    const details = body.details ?? body;
    return normalizeClearance(
        {
            status: details.status ?? CLEARANCE_STATUS.CONFLICT,
            severity: details.severity ?? CLEARANCE_SEVERITY.HIGH,
            utilityCollisions: details.utilityCollisions ?? [],
            temporalConflicts: details.temporalConflicts ?? [],
            evaluationId: details.evaluationId ?? null,
            requiresManualClearance: true,
            degraded: false,
            error: body.message ?? details.error ?? null,
        },
        meta
    );
}
// --- END UPDATE

// --- UPDATE: request lifecycle — dedup, cancellation, generation guard ------
// A single map pin can fire many overlapping evaluations. Two guarantees:
//   • identical in-flight requests share one promise (dedup);
//   • a newer request for the same scope invalidates older ones, so a late
//     reply for a stale coordinate can never render as the current answer.

const _inFlight = new Map();     // dedupKey -> { promise, controller }
const _generation = new Map();   // scopeKey  -> monotonic counter

const dedupKey = (p) =>
    `${p.workOrderId ?? 'adhoc'}|${p.lat.toFixed(6)}|${p.lng.toFixed(6)}|${p.date}|${p.depth}`;

const scopeKeyFor = (params, options) =>
    options.scopeKey ?? `wo:${params.workOrderId ?? 'adhoc'}`;

async function postWithRetry({ payload, requestId, signal, deadlineAt }) {
    let attempt = 0;
    let lastError;

    while (attempt <= CONFIG.MAX_RETRIES) {
        const remaining = deadlineAt - Date.now();
        if (remaining <= 0) {
            const err = lastError ?? new Error('Clearance request exceeded its deadline');
            err.code = err.code ?? 'ECONNABORTED';
            throw err;
        }

        try {
            return await api.post(CONFIG.ENDPOINT, payload, {
                signal,
                timeout: Math.min(CONFIG.REQUEST_TIMEOUT_MS, remaining),
                headers: {
                    // Lets the backend collapse duplicate audit rows if a retry
                    // lands after the original actually succeeded.
                    'X-Idempotency-Key': requestId,
                    'X-Request-Id': requestId,
                },
            });
        } catch (error) {
            lastError = error;
            const kind = classifyTransportError(error);

            // Never retry a cancellation, an auth failure, a validation error,
            // or a 409 — the 409 is a real answer, not a failure.
            const retryable =
                kind === 'NETWORK' ||
                kind === 'TIMEOUT' ||
                (kind === 'SERVER' && CONFIG.RETRYABLE_HTTP.has(error.response?.status)) ||
                CONFIG.RETRYABLE_HTTP.has(error.response?.status);

            if (!retryable || attempt === CONFIG.MAX_RETRIES) throw error;

            const retryAfter = Number(error.response?.headers?.['retry-after']);
            const backoff = Number.isFinite(retryAfter)
                ? retryAfter * 1000
                : CONFIG.RETRY_BASE_DELAY_MS * 2 ** attempt;

            const wait = Math.min(backoff, Math.max(0, deadlineAt - Date.now()));
            if (wait <= 0) throw error;

            await sleep(wait);
            attempt += 1;
        }
    }

    throw lastError;
}
// --- END UPDATE

export const spatialService = {
    /**
     * Evaluates 4D subsurface conflicts for a given coordinate and depth.
     *
     * Resolves to a normalised ClearanceResult and does not throw for transport
     * failures — every non-verified outcome resolves as INDETERMINATE so no
     * upstream `catch` can convert a failed scan into a cleared site.
     *
     * @param {Object} params
     * @param {string} params.workOrderId
     * @param {number} params.latitude
     * @param {number} params.longitude
     * @param {string|Date} params.plannedDate
     * @param {number} [params.excavationDepthM=1.5]
     * @param {Object} [options]
     * @param {AbortSignal} [options.signal]   Caller-owned cancellation.
     * @param {string} [options.scopeKey]      Requests sharing a scope supersede
     *                                         each other (default: per work order).
     * @param {boolean} [options.dedupe=true]  Share one promise across identical
     *                                         concurrent calls.
     * @returns {Promise<Object>} ClearanceResult — never rejects.
     */
    async evaluateSiteClearance(
        { workOrderId, latitude, longitude, plannedDate, excavationDepthM } = {},
        options = {}
    ) {
        const startedAt = Date.now();
        const requestId = newRequestId();

        // --- UPDATE: fail-closed pre-flight gate ---------------------------
        const v = validateParams({ latitude, longitude, plannedDate, excavationDepthM });
        if (v.errors.length > 0) {
            console.warn('[SpatialService] Clearance refused — invalid parameters', {
                requestId, workOrderId, errors: v.errors,
            });
            return baseResult({
                errorKind: 'VALIDATION',
                error: `Cannot evaluate subsurface clearance: ${v.errors.join('; ')}. Manual utility locate required.`,
                durationMs: Date.now() - startedAt,
            });
        }

        // Offline is a scan that did not happen. It is never a clear site.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return baseResult({
                errorKind: 'OFFLINE',
                error: 'Device is offline — subsurface clearance cannot be verified. ' + GENERIC_BLOCK_MESSAGE,
                durationMs: Date.now() - startedAt,
            });
        }
        // --- END UPDATE

        const normalized = { workOrderId, lat: v.lat, lng: v.lng, date: v.date, depth: v.depth };
        const key = dedupKey(normalized);
        const scopeKey = scopeKeyFor(normalized, options);

        if (options.dedupe !== false && _inFlight.has(key)) {
            return _inFlight.get(key).promise;
        }

        // --- UPDATE: supersession — cancel older requests in the same scope --
        const myGeneration = (_generation.get(scopeKey) ?? 0) + 1;
        _generation.set(scopeKey, myGeneration);

        for (const [k, entry] of _inFlight.entries()) {
            if (entry.scopeKey === scopeKey && k !== key) {
                entry.controller.abort();
            }
        }

        const controller = new AbortController();
        if (options.signal) {
            if (options.signal.aborted) controller.abort();
            else options.signal.addEventListener('abort', () => controller.abort(), { once: true });
        }
        // --- END UPDATE

        const payload = {
            workOrderId,
            latitude: v.lat,
            longitude: v.lng,
            plannedDate: v.date,          // local YYYY-MM-DD, never a raw Date
            excavationDepthM: v.depth,
        };

        const run = (async () => {
            try {
                const response = await postWithRetry({
                    payload,
                    requestId,
                    signal: controller.signal,
                    deadlineAt: startedAt + CONFIG.TOTAL_DEADLINE_MS,
                });

                // A reply that arrives after a newer request started must not be
                // rendered — this is the stale-pin race, and it shows a CLEAR
                // badge for a coordinate that was never scanned.
                if (_generation.get(scopeKey) !== myGeneration) {
                    return baseResult({
                        status: CLEARANCE_STATUS.SUPERSEDED,
                        ignorable: true,
                        error: null,
                        errorKind: 'SUPERSEDED',
                        durationMs: Date.now() - startedAt,
                    });
                }

                const result = normalizeClearance(response?.data, {
                    durationMs: Date.now() - startedAt,
                });

                if (result.status !== CLEARANCE_STATUS.CLEAR) {
                    console.warn('[SpatialService] Site not cleared', {
                        requestId,
                        workOrderId,
                        evaluationId: result.evaluationId,
                        status: result.status,
                        severity: result.severity,
                        utilityHits: result.utilityCollisions.length,
                        permitHits: result.temporalConflicts.length,
                    });
                }
                return result;
            } catch (error) {
                const kind = classifyTransportError(error);
                const durationMs = Date.now() - startedAt;

                if (kind === 'CONFLICT_RESPONSE') {
                    return normalizeConflictResponse(error, { durationMs });
                }

                if (kind === 'ABORTED') {
                    // Cancelled by a newer request or by the caller unmounting.
                    // Reported as ignorable so the UI drops it rather than
                    // flashing a spurious blocking banner.
                    return baseResult({
                        status: CLEARANCE_STATUS.SUPERSEDED,
                        ignorable: true,
                        error: null,
                        errorKind: 'ABORTED',
                        durationMs,
                    });
                }

                const messages = {
                    TIMEOUT: 'The clearance scan timed out. ' + GENERIC_BLOCK_MESSAGE,
                    NETWORK: 'Could not reach the clearance service. ' + GENERIC_BLOCK_MESSAGE,
                    UNAUTHENTICATED: 'Your session has expired. Sign in again to verify subsurface clearance.',
                    FORBIDDEN: 'You are not authorised to run subsurface clearance checks.',
                    VALIDATION: error?.response?.data?.message ?? 'The clearance request was rejected as invalid. ' + GENERIC_BLOCK_MESSAGE,
                    SERVER: 'The clearance service failed. ' + GENERIC_BLOCK_MESSAGE,
                    CLIENT: GENERIC_BLOCK_MESSAGE,
                };

                console.error('[SpatialService] Clearance evaluation failed — blocking', {
                    requestId,
                    workOrderId,
                    kind,
                    httpStatus: error?.response?.status ?? null,
                    message: error?.message,
                    durationMs,
                });

                return baseResult({
                    errorKind: kind,
                    error: messages[kind] ?? GENERIC_BLOCK_MESSAGE,
                    durationMs,
                });
            } finally {
                _inFlight.delete(key);
            }
        })();

        _inFlight.set(key, { promise: run, controller, scopeKey });
        return run;
    },

    // --- UPDATE: caller-side helpers ---------------------------------------

    /**
     * The single predicate UI and dispatch code should branch on. Anything that
     * is not a verified CLEAR blocks; SUPERSEDED results are neither blocking
     * nor renderable and should be dropped via `isIgnorable` first.
     */
    isBlocking(result) {
        if (!result || result.ignorable) return false;
        return result.status !== CLEARANCE_STATUS.CLEAR;
    },

    isIgnorable(result) {
        return !!result?.ignorable;
    },

    isVerifiedClear(result) {
        return result?.status === CLEARANCE_STATUS.CLEAR && result.degraded !== true;
    },

    severityRank(result) {
        return SEVERITY_RANK[result?.severity] ?? SEVERITY_RANK.CRITICAL;
    },

    /** Crew-readable one-liner for dispatch cards, toasts and SMS. */
    summarize(result) {
        if (!result) return GENERIC_BLOCK_MESSAGE;
        if (result.ignorable) return '';
        if (result.status === CLEARANCE_STATUS.INDETERMINATE) return result.error ?? GENERIC_BLOCK_MESSAGE;
        if (result.status === CLEARANCE_STATUS.CLEAR) {
            const radius = result.scanRadiusM ? `${result.scanRadiusM} m` : 'the scan radius';
            return result.degraded
                ? `No conflicts reported within ${radius} (unverified — clearance service is out of date).`
                : `No recorded subsurface conflicts within ${radius}.`;
        }

        const parts = [];
        if (result.utilityCollisions.length > 0) {
            const nearest = result.utilityCollisions[0];
            parts.push(
                `${result.utilityCollisions.length} utility line(s); nearest ${nearest?.utility_name || 'unnamed'} at ${nearest?.distance_m ?? '?'} m`
            );
        }
        if (result.temporalConflicts.length > 0) {
            parts.push(`${result.temporalConflicts.length} permit/moratorium conflict(s)`);
        }
        if (result.utilityCollisions.some((u) => u.requires_hand_excavation)) {
            parts.push('HAND EXCAVATION REQUIRED');
        }
        return `[${result.severity}] ${parts.join(' • ')}`;
    },

    /** Cancels every in-flight evaluation for a scope (call on unmount). */
    abortScope(scopeKey) {
        for (const entry of _inFlight.values()) {
            if (entry.scopeKey === scopeKey) entry.controller.abort();
        }
        _generation.set(scopeKey, (_generation.get(scopeKey) ?? 0) + 1);
    },

    abortAll() {
        for (const entry of _inFlight.values()) entry.controller.abort();
        _inFlight.clear();
    },
    // --- END UPDATE
};

export default spatialService;